/**
 * StudyDeepLink — hydrates /ereg/studies/:protocolId (+ /upload) deep links
 * (plan §3.6). eReg's pages select the current study via context, not the URL,
 * so this page looks the study up, selects it, and forwards:
 *   home   → /ereg/folders (carrying return_to via router state)
 *   upload → /ereg/upload?document_type=<role-mapped>&return_to=…
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useEreg } from '../contexts';
import * as eregApi from '../lib/api';
import { primaryBtnCls } from '../../ui';
import { sanitizeReturnTo } from '../../ui/returnTo';
import { ROLE_TO_DOCUMENT_TYPE } from '../../ui/studyRoles';

export function StudyDeepLink({ mode }: { mode: 'home' | 'upload' }) {
  const { protocolId } = useParams<{ protocolId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setCurrentProtocol, setSiteMode, refreshFolders } = useEreg();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!protocolId) return;
      try {
        const protocol = await eregApi.getProtocol(protocolId);
        if (cancelled) return;
        setSiteMode(false);
        setCurrentProtocol(protocol);
        refreshFolders(protocol.id);

        const returnTo = sanitizeReturnTo(searchParams.get('return_to'));
        const returnLabel = searchParams.get('return_label') || undefined;

        if (mode === 'upload') {
          const params = new URLSearchParams();
          const role = searchParams.get('role');
          const mappedType = role ? ROLE_TO_DOCUMENT_TYPE[role] : undefined;
          if (mappedType) params.set('document_type', mappedType);
          if (returnTo) params.set('return_to', returnTo);
          if (returnTo && returnLabel) params.set('return_label', returnLabel);
          const qs = params.toString();
          navigate(`/ereg/upload${qs ? `?${qs}` : ''}`, { replace: true });
        } else {
          navigate('/ereg/folders', {
            replace: true,
            state: returnTo ? { returnTo, returnLabel } : undefined,
          });
        }
      } catch (err: any) {
        if (cancelled) return;
        setError(err?.response?.status === 404 || err?.response?.status === 403
          ? 'This study is not available for your account.'
          : 'Failed to open the study.');
      }
    };
    run();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [protocolId, mode]);

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <p className="text-lg font-medium text-gray-900 dark:text-gray-100">{error}</p>
        <button onClick={() => navigate('/ereg/dashboard')} className={`mt-4 ${primaryBtnCls}`}>
          Go to eReg dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center gap-2 py-16 text-sm text-gray-500 dark:text-gray-400">
      <Loader2 className="h-5 w-5 animate-spin text-primary-600 dark:text-primary-400" />
      Opening study…
    </div>
  );
}

export default StudyDeepLink;
